import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Button,
  InputAdornment, Alert, Fade,
} from '@mui/material';
import SaveIcon         from '@mui/icons-material/SaveRounded';
import ArrowBackIcon    from '@mui/icons-material/ArrowBackRounded';
import InventoryIcon    from '@mui/icons-material/Inventory2Rounded';
import LabelIcon        from '@mui/icons-material/LabelRounded';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupeeRounded';
import StarIcon         from '@mui/icons-material/StarRounded';
import ToggleOnIcon     from '@mui/icons-material/ToggleOnRounded';
import CheckCircleIcon  from '@mui/icons-material/CheckCircleRounded';
import { useNavigate } from "react-router-dom";

const EMPTY_PKG = { name: '', price: '', isDefault: '0', isOn: '1', isActive: '1' };

const NAVY = '#1e3a8a';

const fieldSx = {
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: NAVY,
    marginBottom: '6px',
    display: 'block',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: NAVY,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#f8fafc',
    marginTop: 0,
    '& fieldset': { borderColor: NAVY, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: NAVY },
    '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: 1.5 },
    '& legend': { display: 'none' },
  },
};

const formControlSx = {
  display: 'flex',
  flexDirection: 'column',
  ...fieldSx,
};

export default function PackageMasterForm() {
  const navigate = useNavigate();
  const [newPkg, setNewPkg] = useState(EMPTY_PKG);
  const [alert, setAlert]   = useState(null);

  const setField = (f) => (e) => setNewPkg((p) => ({ ...p, [f]: e.target.value }));

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 3500);
  };

  const handleSave = () => {
    if (!newPkg.name || !newPkg.price) {
      showAlert('warning', 'Package name and price are required.');
      return;
    }
    showAlert('success', `Package "${newPkg.name}" added successfully!`);
    setNewPkg(EMPTY_PKG);
    // TODO: POST to API
    // navigate('/dashboard/package-master');
  };

  return (
    <Box>
      {alert && (
        <Fade in>
          <Alert severity={alert.type} sx={{ mb: 2, borderRadius: 2, fontSize: '0.8125rem' }}>
            {alert.msg}
          </Alert>
        </Fade>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

        {/* Gradient header banner */}
        <Box sx={{
          position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          color: '#fff', px: 3, py: 2.5,
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140,
            borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position: 'absolute', bottom: -50, right: 80, width: 100, height: 100,
            borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2.5,
                background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InventoryIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>Add New Package</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Create a new membership plan
                </Typography>
              </Box>
            </Box>

            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard/package-master')}
              sx={{
                color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
                background: 'rgba(255,255,255,0.12)', borderRadius: 2,
                '&:hover': { background: 'rgba(255,255,255,0.22)' },
              }}>
              Back to list
            </Button>
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Package Name *" size="small" sx={fieldSx}
                value={newPkg.name} onChange={setField('name')} placeholder=""
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LabelIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Price (₹) *" size="small" type="number" sx={fieldSx}
                value={newPkg.price} onChange={setField('price')} placeholder=""
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyRupeeIcon sx={{ fontSize: 18, color: '#10b981' }} />
                    </InputAdornment>
                  ),
                }} />
            </Grid>

            

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel id="active-label">Status</InputLabel>
                <Select
                  labelId="active-label"
                  value={newPkg.isActive}
                  onChange={setField('isActive')}
                  label="Status"
                  notched={false}
                  renderValue={(val) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: val === '1' ? '#10b981' : '#ef4444' }} />
                      {val === '1' ? 'Active' : 'Inactive'}
                    </Box>
                  )}
                >
                  <MenuItem value="1" sx={{ fontSize: '0.8125rem' }}>Active</MenuItem>
                  <MenuItem value="0" sx={{ fontSize: '0.8125rem' }}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0',
            display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => navigate('/dashboard/package-master')}
              sx={{ color: '#64748b', borderColor: '#e2e8f0', border: '1px solid',
                borderRadius: 2, background: '#f8fafc', '&:hover': { background: '#f1f5f9' }, px: 2.5 }}>
              Cancel
            </Button>
            <Button variant="contained" size="small" startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ background: '#6366f1', '&:hover': { background: '#4f46e5' }, borderRadius: 2, px: 2.5 }}>
              Save Package
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}